// src/pages/dashboard/branch/analytics/components/StatsCard.tsx
import React, { memo } from "react";
import { motion } from "framer-motion";
import Skeleton from "../../../../../components/ui/Skeleton";

interface StatsCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ReactNode;
    gradient: string;
    trend?: {
        value: number;
        isPositive: boolean;
    };
    isLoading?: boolean;
    delay?: number;
}

const StatsCard = memo<StatsCardProps>(({
    title,
    value,
    subtitle,
    icon,
    gradient,
    trend,
    isLoading = false,
    delay = 0,
}) => {
    if (isLoading) {
        return (
            <div className="relative overflow-hidden p-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <Skeleton height="h-4" width="w-24" className="mb-3" />
                        <Skeleton height="h-8" width="w-32" className="mb-2" />
                        <Skeleton height="h-3" width="w-16" />
                    </div>
                    <Skeleton height="h-12" width="w-12" rounded="rounded-xl" />
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay, duration: 0.4, ease: "easeOut" }}
            whileHover={{ y: -2, transition: { duration: 0.2 } }}
            className="relative overflow-hidden p-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
        >
            
            {/* Background gradient accent */}
            <div
                className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${gradient} opacity-10 rounded-full blur-2xl -translate-y-8 translate-x-8`}
            />

            <div className="relative flex flex-col items-start justify-between">
                
                 <div className="flex justify-between items-center gap-4 mb-4 w-full">

                     <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                        {title}
                    </p>
                    <div className={` bg-gradient-to-br ${gradient} p-3 rounded-xl shadow-lg inline-flex items-center justify-center`}>
                    <span className="text-white text-xl">{icon}</span>
                </div>
                </div>
                <div className="flex-1">
                   
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                        {value}
                    </p>
                </div>

               
            </div>
        </motion.div>
    );
});

StatsCard.displayName = "StatsCard";

export default StatsCard;
